import React, { Component } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { PowerIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import authService from '../services/authService';
import apiService from '../services/apiService';
import outlookService from '../services/outlookService';
import LoadingSpinner from './common/LoadingSpinner';
import EditMode from './entity/EditMode';
import CreateMode from './entity/CreateMode';
import SearchResults from './entity/SearchResults';

class Home extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchResults: null,
      loading: true,
      checkingMail: true,
      selectedRecord: null,
      showLogoutDialog: false,
      // Global search state
      showGlobalSearch: false,
      globalSearchModule: '',
      globalSearchQuery: '',
      globalSearchResults: [],
      globalSearchLoading: false,
      globalSearchTimeout: null,
      enabledModules: {},
    };
  }

  componentDidMount() {
    this.loadEnabledModules();
    this.checkEmailRecord();
  }

  componentWillUnmount() {
    // Clear search timeout
    if (this.state.globalSearchTimeout) {
      clearTimeout(this.state.globalSearchTimeout);
    }
  }

  loadEnabledModules = () => {
    
    // Get enabled modules from authService
    const enabledModules = authService.getEnabledModules();
    
    this.setState({
      enabledModules: enabledModules,
    });
  };
  checkEmailRecord = async () => {
    try {
      const currentUser = this.props.currentUser || authService.getCurrentUser();

      // Check if user is actually authenticated (not just stored data)
      if (!currentUser || !authService.isAuthenticated()) {
        this.setState({ loading: false, searchResults: null });
        return;
      }

      const fromEmail = outlookService.getFromEmail();
      
      if (!fromEmail) {
        this.setState({ loading: false, searchResults: null });
        return;
      }

      const result = await apiService.getRecordDetailFromEmail(fromEmail);
      
      this.setState({ loading: false });
      
      if (result.success) {
        const searchData = result.data.data || result.data;
        
        // Handle different result scenarios
        if (Array.isArray(searchData) && searchData.length > 0) {
          // Always show search results first, let user choose what to do
          this.setState({ searchResults: searchData });
        } else if (searchData && typeof searchData === 'object') {
          // Single object result - show in list
          this.setState({ searchResults: [searchData] });
        } else {
          // No results - will show module list for creation
          this.setState({ searchResults: null });
        }
      } else if (result.needsLogout) {
        this.handleLogout();
      } else {
        this.setState({ searchResults: null });
      }
    } catch (error) {
      this.setState({ loading: false, searchResults: null });

      if (error.message.includes('not authenticated') || error.message.includes('CRM URL') || error.message.includes('authentication token')) {
        this.handleLogout();
      }
    }
  };

  handleCheckingMail = () => {
    this.setState({ 
      checkingMail: !this.state.checkingMail,
      loading: true,
      searchResults: null,
      selectedRecord: null
    }, () => {
      this.checkEmailRecord();
    });
  };

  handleRecordSelect = (record) => {
    this.setState({ selectedRecord: record });
  };

  handleBackToResults = () => {
    this.setState({ selectedRecord: null });
  };

  handleLogoutClick = () => {
    this.setState({ showLogoutDialog: true });
  };

  handleLogoutConfirm = () => {
    this.setState({ showLogoutDialog: false });
    authService.logout();
  };

  handleLogoutCancel = () => {
    this.setState({ showLogoutDialog: false });
  };

  // Global search methods
  handleGlobalSearchOpen = () => {
    this.setState({ 
      showGlobalSearch: true,
      globalSearchModule: '',
      globalSearchQuery: '',
      globalSearchResults: []
    });
  };

  handleGlobalSearchClose = () => {
    this.setState({ 
      showGlobalSearch: false,
      globalSearchModule: '',
      globalSearchQuery: '',
      globalSearchResults: [],
      globalSearchLoading: false
    });
    
    // Clear timeout
    if (this.state.globalSearchTimeout) {
      clearTimeout(this.state.globalSearchTimeout);
      this.setState({ globalSearchTimeout: null });
    }
  };

  handleGlobalSearchModuleChange = (e) => {
    const module = e.target.value;
    this.setState({
      globalSearchModule: module,
      globalSearchQuery: '',
      globalSearchResults: []
    });
  };

  handleGlobalSearchInputChange = (e) => {
    const query = e.target.value;
    this.setState({ globalSearchQuery: query });

    // Clear existing timeout
    if (this.state.globalSearchTimeout) {
      clearTimeout(this.state.globalSearchTimeout);
    }

    // Clear results if query is too short
    if (query.length < 2) {
      this.setState({ globalSearchResults: [] });
      return;
    }

    // Set new timeout for search
    if (this.state.globalSearchModule) {
      const timeout = setTimeout(() => {
        this.performGlobalSearch(query);
      }, 300); // 300ms debounce
      this.setState({ globalSearchTimeout: timeout });
    }
  };

  performGlobalSearch = async (query) => {
    if (!this.state.globalSearchModule || !query || query.length < 2) {
      return;
    }

    this.setState({ globalSearchLoading: true });

    try {
      
      const result = await apiService.searchRecords(this.state.globalSearchModule, query, 'general');

      if (result.success) {
        const searchData = result.data.data || result.data.records || result.data || [];
        
        // Process the search results to ensure they have the right structure
        const processedResults = Array.isArray(searchData) ? searchData.map(record => ({
          ...record,
          crmid: record.id || record.crmid,
          label: record.label || record.name || 'Unnamed Record',
          module: this.state.globalSearchModule
        })) : [];
        
        this.setState({ globalSearchResults: processedResults });
      } else {
        this.setState({ globalSearchResults: [] });
      }
    } catch (error) {
      this.setState({ globalSearchResults: [] });
    } finally {
      this.setState({ globalSearchLoading: false });
    }
  };

  handleGlobalSearchRecordSelect = (record) => {
    
    // Close global search
    this.handleGlobalSearchClose();
    
    // Open record in detail view
    const viewRecord = {
      ...record,
      viewMode: true,
      crmid: record.crmid || record.id,
      module: record.module || this.state.globalSearchModule
    };
    
    this.setState({ selectedRecord: viewRecord });
  };
  render() {
    const {
      searchResults,
      selectedRecord,
      loading,
      showLogoutDialog,
      showGlobalSearch,
      globalSearchModule,
      globalSearchQuery,
      globalSearchResults,
      globalSearchLoading,
      enabledModules
    } = this.state;
    const currentUser = this.props.currentUser || authService.getCurrentUser();
    
    if (!currentUser) {
      return <LoadingSpinner label="Loading user data..." />;
    }

    const { firstname, lastname } = outlookService.parseDisplayName();
    const fromEmail = outlookService.getFromEmail();

    // Convert enabled modules to array for dropdown
    const moduleOptions = Object.entries(enabledModules).map(([key, moduleData]) => ({
      value: key,
      label: moduleData.singular_modname || moduleData.display_name || moduleData.label || key
    }));
    if (loading) {
      return (
        <LoadingSpinner label="Checking email records..." variant="fullscreen" />
      );
    }

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Global Search Dialog */}
        <Transition appear show={showGlobalSearch} as={React.Fragment}>
          <Dialog as="div" className="relative z-50" onClose={this.handleGlobalSearchClose}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-start justify-center p-4 pt-16">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                      <div className="flex items-center">
                        <MagnifyingGlassIcon className="h-6 w-6 text-primary-600 mr-3" />
                        <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                          Search Records
                        </Dialog.Title>
                      </div>
                      <button
                        onClick={this.handleGlobalSearchClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-4">
                      {/* Module Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Module
                        </label>
                        <select
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white shadow-sm"
                          value={globalSearchModule}
                          onChange={this.handleGlobalSearchModuleChange}
                        >
                          <option value="">Choose a module...</option>
                          {moduleOptions.map((module) => (
                            <option key={module.value} value={module.value}>
                              {module.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Search Input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Search Records
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white shadow-sm"
                            placeholder={globalSearchModule ? `Search ${globalSearchModule} records...` : "Select a module first"}
                            value={globalSearchQuery}
                            onChange={this.handleGlobalSearchInputChange}
                            disabled={!globalSearchModule}
                          />
                          <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                            {globalSearchLoading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                            ) : (
                              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          Type at least 2 characters to search
                        </p>
                      </div>

                      {/* Search Results */}
                      <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50">
                        {globalSearchLoading ? (
                          <div className="p-8 text-center bg-white">
                            <div className="flex flex-col items-center space-y-3">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                              <span className="text-sm text-gray-600">Searching records...</span>
                            </div>
                          </div>
                        ) : globalSearchResults.length > 0 ? (
                          <div className="bg-white space-y-2 p-2">
                            {globalSearchResults.map((record, index) => (
                              <button
                                key={index}
                                className="w-full px-3 py-3 text-left hover:bg-blue-50 hover:border-blue-200 transition-all duration-200 focus:outline-none focus:bg-blue-50 border border-gray-200 rounded-lg bg-white shadow-sm mx-1 my-1"
                                onClick={() => this.handleGlobalSearchRecordSelect(record)}
                              >
                                <div className="space-y-1.5">
                                  {/* Record Name - Bold and Prominent */}
                                  <div>
                                    <h4 className="text-sm font-bold text-gray-900 leading-tight">
                                      {record.label || record.name}
                                    </h4>
                                  </div>
                                  
                                  {/* Header Fields - Enhanced Display */}
                                  {record.header_fields && record.header_fields.length > 0 && (
                                    <div className="space-y-1">
                                      {record.header_fields.slice(0, 2).map((field, fieldIndex) => (
                                        field.value && field.value.trim() !== '' && (
                                          <div key={fieldIndex} className="flex flex-col">
                                            <span className="text-xs font-semibold text-gray-700 mb-0.5">
                                              {field.fieldlabel}:
                                            </span>
                                            <span className="text-xs text-gray-800 font-medium break-words leading-relaxed">
                                              {field.value}
                                            </span>
                                          </div>
                                        )
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : globalSearchQuery.length >= 2 && globalSearchModule ? (
                          <div className="p-6 text-center bg-white">
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-gray-600">No records found</p>
                              <p className="text-xs text-gray-500">
                                No records found for "<span className="font-medium">{globalSearchQuery}</span>" in {globalSearchModule}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="p-6 text-center bg-white">
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-gray-600">
                                {!globalSearchModule 
                                  ? "Select a module to start searching"
                                  : "Start typing to search records"
                                }
                              </p>
                              <p className="text-xs text-gray-500">
                                {!globalSearchModule 
                                  ? "Choose a module from the dropdown above"
                                  : "Type at least 2 characters to search"
                                }
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Logout Confirmation Dialog */}
        <Transition appear show={showLogoutDialog} as={React.Fragment}>
          <Dialog as="div" className="relative z-10" onClose={this.handleLogoutCancel}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      Confirm Logout
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to logout?
                      </p>
                    </div>

                    <div className="mt-4 flex gap-3 justify-end">
                      <button
                        type="button"
                        className="border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200 text-sm"
                        onClick={this.handleLogoutCancel}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 text-sm"
                        style={{ backgroundColor: '#0075a8' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#005e86'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#0075a8'}
                        onClick={this.handleLogoutConfirm}
                      >
                        Logout
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center">

              <span className="text-base font-semibold text-gray-900">
                Welcome {currentUser?.username}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={this.handleGlobalSearchOpen}
                title="Search Records"
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
              </button>
              <button
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={this.handleLogoutClick}
                title="Logout"
              >
                <PowerIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-4">
          {searchResults && searchResults.length > 0 ? (
            selectedRecord ? (
              <EditMode
                entity_info={selectedRecord}
                firstname={firstname}
                lastname={lastname}
                fromEmail={fromEmail}
                onCheckingMail={this.handleCheckingMail}
                onLogout={this.handleLogoutConfirm}
                onBack={this.handleBackToResults}
              />
            ) : (
              <SearchResults
                results={searchResults}
                onRecordSelect={this.handleRecordSelect}
                onCheckingMail={this.handleCheckingMail}
                onLogout={this.handleLogoutConfirm}
              />
            )
          ) : selectedRecord ? (
            <EditMode
              entity_info={selectedRecord}
              firstname={firstname}
              lastname={lastname}
              fromEmail={fromEmail}
              onCheckingMail={this.handleCheckingMail}
              onLogout={this.handleLogoutConfirm}
              onBack={null}
            />
          ) : (
            <SearchResults
              results={[]}
              onRecordSelect={this.handleRecordSelect}
              onCheckingMail={this.handleCheckingMail}
              onLogout={this.handleLogoutConfirm}
            />
          )}
        </div>
      </div>
    );
  }
}

export default Home;
